import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Braces, Check, ChevronDown, ChevronRight, Clipboard, Code2, Download, Eye, FileJson, Link2, X } from 'lucide-react';

export interface StepDataInspectorPanel {
    id: string;
    label: string;
    value: unknown;
}

export interface StepDataInspectorProps {
    title: string;
    subtitle?: string;
    panels: StepDataInspectorPanel[];
    initialPanelId?: string;
    onClose: () => void;
}

type DataPath = Array<string | number>;

const isHiddenRenderedField = (path: DataPath, key: string): boolean => (
    path.length >= 2
    && path[path.length - 2] === 'detail'
    && path[path.length - 1] === 'bucket'
    && key === 'name'
);

const parseSerializedValue = (value: unknown): unknown => {
    let current = value;
    for (let depth = 0; depth < 2 && typeof current === 'string'; depth++) {
        const trimmed = current.trim();
        if (!trimmed || (!trimmed.startsWith('{') && !trimmed.startsWith('[') && trimmed !== 'true' && trimmed !== 'false' && trimmed !== 'null')) break;
        try {
            current = JSON.parse(trimmed);
        } catch {
            break;
        }
    }
    return current;
};

const rawText = (value: unknown): string => {
    const parsed = parseSerializedValue(value);
    if (typeof parsed === 'string') return parsed;
    try {
        return JSON.stringify(parsed, null, 2);
    } catch {
        return String(parsed);
    }
};

const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const isScalar = (value: unknown) => value === null || ['string', 'number', 'boolean'].includes(typeof value);

const valueAtPath = (value: unknown, path: DataPath): unknown => path.reduce<unknown>((current, segment) => {
    const parsed = parseSerializedValue(current);
    if (Array.isArray(parsed) && typeof segment === 'number') return parsed[segment];
    if (isRecord(parsed) && typeof segment === 'string') return parsed[segment];
    return parsed;
}, value);

const isHttpURL = (value: string): boolean => {
    try {
        const url = new URL(value);
        return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
        return false;
    }
};

const isImageSource = (value: string): boolean => {
    if (value.startsWith('data:image/')) return true;
    if (!isHttpURL(value)) return false;
    try {
        return /\.(png|jpe?g|gif|webp|svg|avif)$/i.test(new URL(value).pathname);
    } catch {
        return false;
    }
};

const dataImageSource = (item: Record<string, unknown>): string | null => {
    const direct = item.url || item.image_url || item.imageUrl;
    if (typeof direct === 'string' && isImageSource(direct)) return direct;
    if (typeof item.data === 'string' && typeof item.mimeType === 'string' && item.mimeType.startsWith('image/')) {
        return item.data.startsWith('data:') ? item.data : `data:${item.mimeType};base64,${item.data}`;
    }
    return null;
};

const displayKey = (key: string): string => key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, character => character.toUpperCase());

const looksLikeMarkdown = (value: string) => value.includes('\n') || /(^|\s)(#{1,6}\s|[-*]\s|\d+\.\s|\|.+\||\*\*[^*]+\*\*)/m.test(value);
const looksLikeHTML = (value: string) => /^\s*<(?:!doctype|html|body|div|p|table|section|article|h[1-6]|ul|ol)\b/i.test(value);

const MarkdownValue = ({ value }: { value: string }) => (
    <div className="step-output-markdown max-w-none break-words text-[14px] leading-6 text-slate-700">
        <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
                h1: ({ children, ...props }) => <h1 className="mb-3 mt-5 text-xl font-bold text-slate-950 first:mt-0" {...props}>{children}</h1>,
                h2: ({ children, ...props }) => <h2 className="mb-2 mt-5 text-lg font-bold text-slate-950 first:mt-0" {...props}>{children}</h2>,
                h3: ({ children, ...props }) => <h3 className="mb-2 mt-4 text-base font-bold text-slate-900 first:mt-0" {...props}>{children}</h3>,
                p: props => <p className="mb-3 last:mb-0" {...props} />,
                ul: props => <ul className="mb-3 list-disc space-y-1 pl-5" {...props} />,
                ol: props => <ol className="mb-3 list-decimal space-y-1 pl-5" {...props} />,
                blockquote: props => <blockquote className="my-3 border-l-2 border-orange-500 bg-orange-50 px-4 py-2 text-slate-700" {...props} />,
                a: ({ children, ...props }) => <a className="font-medium text-blue-700 underline decoration-blue-300 underline-offset-2 hover:text-blue-900" {...props} target="_blank" rel="noreferrer">{children}</a>,
                code: ({ className, children, ...props }) => className
                    ? <code className={`${className} block overflow-x-auto rounded-md bg-slate-950 p-4 font-mono text-xs leading-5 text-slate-100`} {...props}>{children}</code>
                    : <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[12px] text-slate-800" {...props}>{children}</code>,
                table: props => <table className="w-full border-collapse text-left text-xs" {...props} />,
                th: props => <th className="border-b border-slate-300 bg-slate-100 px-3 py-2 font-bold text-slate-700" {...props} />,
                td: props => <td className="border-b border-slate-200 px-3 py-2 align-top text-slate-700" {...props} />,
                img: props => <img className="my-3 max-h-96 max-w-full rounded-lg border border-slate-200 object-contain" alt={props.alt || 'Workflow output'} {...props} />,
            }}
        >
            {value}
        </ReactMarkdown>
    </div>
);

const ScalarValue = ({ value }: { value: unknown }) => {
    if (value === null || value === undefined) return <span className="font-mono text-xs italic text-slate-400">null</span>;
    if (typeof value === 'boolean') {
        return <span className={`inline-flex rounded-full px-2 py-0.5 font-mono text-xs font-bold ${value ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>{String(value)}</span>;
    }
    if (typeof value === 'number') return <span className="font-mono text-sm font-semibold tabular-nums text-violet-800">{value.toLocaleString()}</span>;
    const text = String(value);
    if (isImageSource(text)) {
        return (
            <a href={text} target="_blank" rel="noreferrer" className="block w-fit">
                <img src={text} alt="Workflow output" className="max-h-96 max-w-full rounded-lg border border-slate-200 bg-white object-contain shadow-sm" />
            </a>
        );
    }
    if (isHttpURL(text)) {
        return <a href={text} target="_blank" rel="noreferrer" className="inline-flex max-w-full items-center gap-1.5 break-all font-medium text-blue-700 underline decoration-blue-300 underline-offset-2 hover:text-blue-900"><Link2 className="h-3.5 w-3.5 shrink-0" />{text}</a>;
    }
    if (looksLikeHTML(text)) return <pre className="overflow-x-auto whitespace-pre-wrap rounded-md bg-slate-950 p-4 font-mono text-xs leading-5 text-slate-100"><code>{text}</code></pre>;
    if (looksLikeMarkdown(text)) return <MarkdownValue value={text} />;
    return <span className="whitespace-pre-wrap break-words text-sm leading-6 text-slate-800">{text}</span>;
};

const UniformObjectTable = ({ rows }: { rows: Record<string, unknown>[] }) => {
    const columns = Array.from(new Set(rows.flatMap(row => Object.keys(row))));
    return (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="min-w-full border-collapse text-left text-xs">
                <thead className="sticky top-0 bg-slate-100">
                    <tr>{columns.map(column => <th key={column} className="border-b border-slate-300 px-3 py-2 font-bold text-slate-700">{displayKey(column)}</th>)}</tr>
                </thead>
                <tbody>
                    {rows.map((row, index) => (
                        <tr key={index} className="odd:bg-white even:bg-slate-50/70">
                            {columns.map(column => <td key={column} className="max-w-xs border-b border-slate-100 px-3 py-2 align-top"><ScalarValue value={row[column]} /></td>)}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const MessageList = ({ messages, path, onFocus }: { messages: Record<string, unknown>[]; path: DataPath; onFocus: (path: DataPath) => void }) => (
    <div className="space-y-3">
        {messages.map((message, messageIndex) => {
            const content = Array.isArray(message.content) ? message.content : [message.content ?? message.text ?? ''];
            return (
                <article key={messageIndex} className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                    <header className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-4 py-2">
                        <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-600">{String(message.role || 'Message')}</span>
                        {typeof message.timestamp === 'string' && <span className="text-xs text-slate-400">{message.timestamp}</span>}
                    </header>
                    <div className="space-y-3 p-4">
                        {content.map((item, contentIndex) => {
                            if (isRecord(item)) {
                                const image = dataImageSource(item);
                                return (
                                    <div key={contentIndex}>
                                        {typeof item.text === 'string' && <MarkdownValue value={item.text} />}
                                        {image && <img src={image} alt="Model output" className="mt-3 max-h-96 max-w-full rounded-lg border border-slate-200 object-contain" />}
                                        {Array.isArray(item.images) && item.images.map((candidate, imageIndex) => {
                                            const source = isRecord(candidate) ? dataImageSource(candidate) : null;
                                            return source ? <img key={imageIndex} src={source} alt="Model output" className="mt-3 max-h-96 max-w-full rounded-lg border border-slate-200 object-contain" /> : null;
                                        })}
                                        {Array.isArray(item.fileCitations) && item.fileCitations.length > 0 && (
                                            <details className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3">
                                                <summary className="cursor-pointer text-xs font-semibold text-slate-600">{item.fileCitations.length} source citation{item.fileCitations.length === 1 ? '' : 's'}</summary>
                                                <div className="mt-3"><ValueRenderer value={item.fileCitations} path={[...path, messageIndex, 'content', contentIndex, 'fileCitations']} onFocus={onFocus} /></div>
                                            </details>
                                        )}
                                        {Object.keys(item).some(key => !['text', 'images', 'fileCitations'].includes(key)) && (
                                            <details className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3">
                                                <summary className="cursor-pointer text-xs font-semibold text-slate-600">Content metadata</summary>
                                                <div className="mt-3"><ValueRenderer value={Object.fromEntries(Object.entries(item).filter(([key]) => !['text', 'images', 'fileCitations'].includes(key)))} path={[...path, messageIndex, 'content', contentIndex]} onFocus={onFocus} /></div>
                                            </details>
                                        )}
                                    </div>
                                );
                            }
                            return <ScalarValue key={contentIndex} value={item} />;
                        })}
                        {Object.keys(message).some(key => !['role', 'content', 'text', 'timestamp', 'id'].includes(key)) && (
                            <details className="rounded-md border border-slate-200 bg-slate-50 p-3">
                                <summary className="cursor-pointer text-xs font-semibold text-slate-600">Message metadata</summary>
                                <div className="mt-3"><ValueRenderer value={Object.fromEntries(Object.entries(message).filter(([key]) => !['role', 'content', 'text'].includes(key)))} path={[...path, messageIndex]} onFocus={onFocus} /></div>
                            </details>
                        )}
                    </div>
                </article>
            );
        })}
    </div>
);

const ValueRenderer = ({ value, path, onFocus, depth = 0 }: { value: unknown; path: DataPath; onFocus: (path: DataPath) => void; depth?: number }) => {
    const parsed = parseSerializedValue(value);
    if (isScalar(parsed) || parsed === undefined) return <ScalarValue value={parsed} />;

    if (Array.isArray(parsed)) {
        if (parsed.length === 0) return <span className="font-mono text-xs text-slate-400">Empty list</span>;
        if (parsed.every(item => isRecord(item) && 'role' in item && ('content' in item || 'text' in item))) {
            return <MessageList messages={parsed as Record<string, unknown>[]} path={path} onFocus={onFocus} />;
        }
        const records = parsed.filter(isRecord);
        const columns = records.length === parsed.length ? Array.from(new Set(records.flatMap(row => Object.keys(row)))) : [];
        if (records.length === parsed.length && columns.length > 0 && columns.length <= 8 && records.every(row => Object.values(row).every(isScalar))) {
            return <UniformObjectTable rows={records} />;
        }
        return (
            <div className="space-y-2">
                {parsed.map((item, index) => (
                    <CollapsibleValue key={index} label={`Item ${index + 1}`} value={item} path={[...path, index]} onFocus={onFocus} defaultOpen={depth < 1} depth={depth + 1} />
                ))}
            </div>
        );
    }

    if (isRecord(parsed)) {
        const image = dataImageSource(parsed);
        if (image && Object.keys(parsed).every(key => ['url', 'image_url', 'imageUrl', 'data', 'mimeType', 'detail'].includes(key))) {
            return <img src={image} alt="Workflow output" className="max-h-96 max-w-full rounded-lg border border-slate-200 bg-white object-contain shadow-sm" />;
        }
        // Event bucket names are infrastructure details. Keep them available in
        // Raw JSON for debugging, but omit them from the human-readable view.
        const entries = Object.entries(parsed).filter(([key]) => !isHiddenRenderedField(path, key));
        if (entries.length === 0) return <span className="font-mono text-xs text-slate-400">Empty object</span>;
        return (
            <dl className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
                {entries.map(([key, item]) => {
                    const complex = item !== null && typeof parseSerializedValue(item) === 'object';
                    return (
                        <div key={key} className={complex ? 'p-3 sm:p-4' : 'grid gap-1.5 p-3 sm:grid-cols-[minmax(130px,0.34fr)_minmax(0,1fr)] sm:gap-4 sm:p-4'}>
                            <dt>
                                <button type="button" onClick={() => onFocus([...path, key])} className="break-all text-left font-mono text-[11px] font-bold uppercase tracking-wide text-slate-500 hover:text-orange-700">{displayKey(key)}</button>
                            </dt>
                            <dd className={complex ? 'mt-3 min-w-0' : 'min-w-0'}><ValueRenderer value={item} path={[...path, key]} onFocus={onFocus} depth={depth + 1} /></dd>
                        </div>
                    );
                })}
            </dl>
        );
    }

    return <ScalarValue value={String(parsed)} />;
};

const CollapsibleValue = ({ label, value, path, onFocus, defaultOpen, depth }: { label: string; value: unknown; path: DataPath; onFocus: (path: DataPath) => void; defaultOpen: boolean; depth: number }) => {
    const [open, setOpen] = useState(defaultOpen);
    const scalar = isScalar(parseSerializedValue(value));
    if (scalar) return <div className="flex items-start gap-3 rounded-md border border-slate-200 bg-white px-3 py-2"><span className="font-mono text-[11px] font-bold text-slate-400">{label}</span><ScalarValue value={parseSerializedValue(value)} /></div>;
    return (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <button type="button" onClick={() => { setOpen(current => !current); onFocus(path); }} className="flex w-full items-center gap-2 bg-slate-50 px-3 py-2 text-left text-xs font-bold text-slate-700 hover:bg-slate-100">
                {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}{label}
            </button>
            {open && <div className="p-3"><ValueRenderer value={value} path={path} onFocus={onFocus} depth={depth} /></div>}
        </div>
    );
};

export function StepDataInspector({ title, subtitle, panels, initialPanelId, onClose }: StepDataInspectorProps) {
    const [activePanelID, setActivePanelID] = useState(initialPanelId || panels[0]?.id || '');
    const [view, setView] = useState<'rendered' | 'raw'>('rendered');
    const [focusPath, setFocusPath] = useState<DataPath>([]);
    const [copied, setCopied] = useState(false);
    const activePanel = panels.find(panel => panel.id === activePanelID) || panels[0];
    const activeValue = useMemo(() => parseSerializedValue(activePanel?.value), [activePanel?.value]);
    const focusedValue = useMemo(() => valueAtPath(activeValue, focusPath), [activeValue, focusPath]);
    const serialized = useMemo(() => rawText(activePanel?.value), [activePanel?.value]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose]);

    if (!activePanel) return null;

    const copyValue = async () => {
        await navigator.clipboard.writeText(serialized);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1600);
    };

    const downloadValue = () => {
        const blob = new Blob([serialized], { type: 'application/json;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${activePanel.id}.json`;
        anchor.click();
        URL.revokeObjectURL(url);
    };

    return createPortal(
        <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-slate-950/60 p-2 backdrop-blur-[2px] sm:p-6" onMouseDown={event => event.target === event.currentTarget && onClose()}>
            <section role="dialog" aria-modal="true" aria-labelledby="step-data-inspector-title" className="flex h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl border border-slate-300 bg-slate-100 shadow-2xl sm:h-[88vh]">
                <div className="grid grid-cols-[6px_minmax(0,1fr)]">
                    <div className="bg-orange-600" />
                    <header className="flex min-w-0 items-start justify-between gap-4 border-b border-slate-200 bg-white px-4 py-4 sm:px-6">
                        <div className="min-w-0">
                            <div className="mb-1 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-orange-700"><FileJson className="h-4 w-4" /> Execution record</div>
                            <h2 id="step-data-inspector-title" className="truncate text-xl font-bold text-slate-950 sm:text-2xl">{title}</h2>
                            {subtitle && <p className="mt-1 truncate text-sm text-slate-500">{subtitle}</p>}
                        </div>
                        <button type="button" onClick={onClose} className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-950" aria-label="Close inspector"><X className="h-5 w-5" /></button>
                    </header>
                </div>

                <div className="flex flex-col justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:px-6">
                    <div className="flex gap-1 rounded-md bg-slate-100 p-1">
                        {panels.map(panel => <button key={panel.id} type="button" onClick={() => { setActivePanelID(panel.id); setFocusPath([]); }} className={`rounded px-3 py-1.5 text-xs font-bold transition ${panel.id === activePanel.id ? 'bg-white text-orange-800 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`}>{panel.label}</button>)}
                    </div>
                    <div className="flex items-center gap-1.5">
                        <div className="mr-1 flex rounded-md border border-slate-200 p-0.5">
                            <button type="button" onClick={() => setView('rendered')} className={`inline-flex items-center gap-1.5 rounded px-2.5 py-1.5 text-xs font-semibold ${view === 'rendered' ? 'bg-slate-800 text-white' : 'text-slate-500 hover:bg-slate-100'}`}><Eye className="h-3.5 w-3.5" />Rendered</button>
                            <button type="button" onClick={() => setView('raw')} className={`inline-flex items-center gap-1.5 rounded px-2.5 py-1.5 text-xs font-semibold ${view === 'raw' ? 'bg-slate-800 text-white' : 'text-slate-500 hover:bg-slate-100'}`}><Code2 className="h-3.5 w-3.5" />Raw JSON</button>
                        </div>
                        <button type="button" onClick={copyValue} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100">{copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Clipboard className="h-3.5 w-3.5" />}{copied ? 'Copied' : 'Copy'}</button>
                        <button type="button" onClick={downloadValue} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100"><Download className="h-3.5 w-3.5" />Download</button>
                    </div>
                </div>

                <nav aria-label="Output path" className="flex min-h-10 items-center gap-1 overflow-x-auto border-b border-slate-200 bg-slate-50 px-4 py-2 font-mono text-[11px] text-slate-500 sm:px-6">
                    <Braces className="mr-1 h-3.5 w-3.5 shrink-0 text-orange-700" />
                    <button type="button" onClick={() => setFocusPath([])} className="font-bold text-slate-700 hover:text-orange-700">{activePanel.label.toLowerCase()}</button>
                    {focusPath.map((segment, index) => <span key={`${segment}-${index}`} className="flex items-center gap-1"><ChevronRight className="h-3 w-3" /><button type="button" onClick={() => setFocusPath(focusPath.slice(0, index + 1))} className="whitespace-nowrap hover:text-orange-700">{segment}</button></span>)}
                </nav>

                <main className="min-h-0 flex-1 overflow-auto p-4 sm:p-6">
                    {view === 'rendered'
                        ? <ValueRenderer value={focusedValue} path={focusPath} onFocus={setFocusPath} />
                        : <pre className="min-h-full overflow-auto rounded-lg bg-slate-950 p-4 font-mono text-xs leading-5 text-slate-100 sm:p-5"><code>{serialized}</code></pre>}
                </main>
            </section>
        </div>,
        document.body,
    );
}

export function StepOutputButton({ output, title, subtitle, workflowID, stepID }: { output?: unknown; title: string; subtitle?: string; workflowID?: string; stepID?: string }) {
    const [open, setOpen] = useState(false);
    const [liveOutput, setLiveOutput] = useState(output);

    useEffect(() => setLiveOutput(output), [output]);
    useEffect(() => {
        if (!workflowID || !stepID) return;
        const updateOutput = (event: Event) => {
            const detail = (event as CustomEvent<{
                workflowID: string;
                stepLogs: Array<{ stepId: string; status: string; output: unknown }>;
            }>).detail;
            if (!detail || detail.workflowID !== workflowID) return;
            const stepLog = detail.stepLogs.find(log => log.stepId === stepID);
            if (stepLog?.status === 'success') setLiveOutput(stepLog.output);
        };
        window.addEventListener('workflow-step-outputs-updated', updateOutput);
        return () => window.removeEventListener('workflow-step-outputs-updated', updateOutput);
    }, [stepID, workflowID]);

    if (liveOutput === undefined || liveOutput === null || liveOutput === '') return null;
    return (
        <>
            <button type="button" onClick={() => setOpen(true)} className="nodrag ml-2 inline-flex h-6 items-center gap-1 rounded border border-orange-200 bg-orange-50 px-1.5 text-[10px] font-bold uppercase tracking-wide text-orange-800 hover:border-orange-400 hover:bg-orange-100" title="View step output">
                <Eye className="h-3.5 w-3.5" /> Output
            </button>
            {open && <StepDataInspector title={title} subtitle={subtitle} panels={[{ id: 'output', label: 'Output', value: liveOutput }]} onClose={() => setOpen(false)} />}
        </>
    );
}
